import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { css, cx } from '@emotion/css';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import Select, { components } from 'react-select';
import mathImg from '../../assets/gen_images/mathematics.png';
import generalMathImg from '../../assets/gen_images/general_math.png';

// ─── Design Tokens ──────────────────────────────────────────────────────────
const ORANGE = '#f16602';
const ORANGE_LIGHT = 'rgba(241,102,2,0.12)';
const ORANGE_GLOW = 'rgba(241,102,2,0.28)';
const CREAM = '#fdfbf7';
const CREAM_DARK = '#f5efe6';
const TEXT_DARK = '#1a1a1a';
const TEXT_MID = '#4a4a4a';
const TEXT_LIGHT = '#888';

// ─── Emotion Styles ──────────────────────────────────────────────────────────

const pageStyles = css`
  min-height: 100vh;
  background: ${CREAM};
  font-family: 'Inter', 'Outfit', sans-serif;
  padding-bottom: 80px;
`;

const heroStyles = css`
  position: relative;
  width: 100%;
  background: linear-gradient(135deg, #1a0a00 0%, #3d1200 50%, #5c2200 100%);
  overflow: hidden;
  padding: 56px 40px 48px;
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 767px) {
    padding: 40px 20px 36px;
    flex-direction: column;
    text-align: center;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 10% 50%, rgba(241,102,2,0.22) 0%, transparent 55%),
      radial-gradient(ellipse at 90% 20%, rgba(255,160,60,0.12) 0%, transparent 50%);
    pointer-events: none;
  }

  /* floating orbs */
  &::after {
    content: '';
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(241,102,2,0.14) 0%, transparent 70%);
    right: -60px;
    top: -80px;
    pointer-events: none;
  }
`;

const heroImageWrap = css`
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: rgba(255,255,255,0.07);
  backdrop-filter: blur(8px);
  border: 1.5px solid rgba(241,102,2,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 40px rgba(241,102,2,0.25);

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
    filter: drop-shadow(0 4px 16px rgba(241,102,2,0.5));
  }

  @media (max-width: 767px) {
    width: 90px;
    height: 90px;
    img { width: 60px; height: 60px; }
  }
`;

const heroBadge = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(241,102,2,0.18);
  border: 1px solid rgba(241,102,2,0.4);
  color: #ffaa55;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 10px;

  span { width: 6px; height: 6px; border-radius: 50%; background: ${ORANGE}; animation: pulse 1.8s infinite; }
  @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
`;

const heroTitle = css`
  color: #fff;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 0 0 6px;
  line-height: 1.15;
  text-shadow: 0 2px 16px rgba(0,0,0,0.4);

  @media (max-width: 767px) { font-size: 1.5rem; }
`;

const heroSub = css`
  color: rgba(255,255,255,0.6);
  font-size: 0.92rem;
  margin: 0;
`;

const contentWrap = css`
  max-width: 720px;
  margin: 0 auto;
  padding: 36px 20px 0;
`;

const cardStyles = css`
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(241,102,2,0.15);
  box-shadow: 0 8px 40px rgba(0,0,0,0.07);
  padding: 32px 28px;
  margin-bottom: 20px;

  @media (max-width: 600px) { padding: 22px 16px; }
`;

const sectionLabel = css`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${ORANGE};
  margin-bottom: 10px;
`;

const sectionTitle = css`
  font-size: 1rem;
  font-weight: 600;
  color: ${TEXT_DARK};
  margin-bottom: 16px;
`;

const divider = css`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, ${ORANGE_LIGHT}, transparent);
  margin: 22px 0;
`;

const rowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  &:last-child { margin-bottom: 0; }
`;

const rowLabel = css`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${TEXT_DARK};
  flex-shrink: 0;
`;

const switchWrap = css`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;

  input { opacity: 0; width: 0; height: 0; }

  .slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: #d0d0d0;
    border-radius: 34px;
    transition: 0.35s;
  }
  .slider:before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.35s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
  input:checked + .slider { background: ${ORANGE}; }
  input:checked + .slider:before { transform: translateX(20px); }
`;

const sliderContainerStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
`;

const numInputStyles = {
  width: '60px',
  textAlign: 'center',
  fontWeight: 700,
  fontSize: '0.9rem',
  padding: '6px 4px',
  border: `1.5px solid rgba(241,102,2,0.3)`,
  borderRadius: '8px',
  background: '#fff',
  color: '#1a1a1a',
  outline: 'none',
  flexShrink: 0,
};

const timeSelectStyles = css`
  select {
    border: 1.5px solid rgba(241,102,2,0.4);
    border-radius: 10px;
    padding: 8px 14px;
    font-weight: 600;
    font-size: 0.9rem;
    color: ${ORANGE};
    background: #fff;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;

    &:hover, &:focus { border-color: ${ORANGE}; }
  }
`;

const totalTimeBar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, ${ORANGE_LIGHT}, rgba(255,200,100,0.08));
  border: 1px solid rgba(241,102,2,0.2);
  border-radius: 12px;
  padding: 12px 18px;
  margin-top: 8px;

  span:first-child {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${TEXT_MID};
  }
  span:last-child {
    font-size: 0.95rem;
    font-weight: 800;
    color: ${ORANGE};
  }
`;

const startBtn = css`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, ${ORANGE} 0%, #d94f00 100%);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(241,102,2,0.35);
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 32px rgba(241,102,2,0.45);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled {
    background: #ccc;
    box-shadow: none;
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const emptyHint = css`
  text-align: center;
  font-size: 0.88rem;
  color: ${TEXT_LIGHT};
  margin-top: 8px;
`;

// ─── react-select custom styles ──────────────────────────────────────────────
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused
      ? `2px solid ${ORANGE}`
      : '2px solid rgba(241,102,2,0.25)',
    borderRadius: '12px',
    minHeight: '50px',
    boxShadow: state.isFocused ? `0 0 0 3px ${ORANGE_GLOW}` : '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'border 0.2s, box-shadow 0.2s',
    '&:hover': { borderColor: ORANGE },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 32px rgba(0,0,0,0.12)',
    border: '1px solid rgba(241,102,2,0.15)',
  }),
  option: (provided, state) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    backgroundColor: state.isSelected
      ? ORANGE_LIGHT
      : state.isFocused
      ? 'rgba(241,102,2,0.06)'
      : '#fff',
    color: state.isSelected ? ORANGE : TEXT_DARK,
    fontWeight: state.isSelected ? 700 : 500,
    cursor: 'pointer',
    '&:active': {
      backgroundColor: ORANGE_LIGHT,
    },
  }),
  multiValue: (s) => ({ display: 'none' }),
  multiValueLabel: (s) => ({ display: 'none' }),
  multiValueRemove: (s) => ({ display: 'none' }),
  placeholder: (s) => ({ ...s, color: '#aaa' }),
};

// ─── Multi-select subcomponents ──────────────────────────────────────────────
const Option = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', pointerEvents: 'none' }}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{
          margin: 0,
          padding: 0,
          width: '16px',
          height: '16px',
          accentColor: ORANGE,
          cursor: 'pointer',
          flexShrink: 0,
          verticalAlign: 'middle',
        }}
      />
      <span style={{ fontSize: '0.92rem', lineHeight: 1.3, color: 'inherit' }}>{props.label}</span>
    </div>
  </components.Option>
);

const ValueContainer = ({ children, ...props }) => {
  const { getValue, hasValue } = props;
  const n = getValue().length;
  return (
    <components.ValueContainer {...props}>
      {hasValue ? (
        <span style={{ fontWeight: 700, color: ORANGE, marginRight: 6 }}>
          {n} topic{n > 1 ? 's' : ''} selected
        </span>
      ) : null}
      {children}
    </components.ValueContainer>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
function SubCategorySelection() {
  const [subCategories, setSubCategories] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const history = useHistory();

  const [startLimit, setStartLimit] = useState(1);
  const [endLimit, setEndLimit] = useState(1);
  const [random, setRandom] = useState(true);
  const [timePerQuestion, setTimePerQuestion] = useState(60);

  const { category, level } = location.state;
  const timeOptions = [0, 5, 10, 15, 30, 45, 60, 90, 120, 180, 300];

  const filteredQuestions = useMemo(() => {
    const selectedValues = new Set(selectedOptions.map(opt => opt.value));
    return originalQuestions.filter(q => selectedValues.has(q.subcategory));
  }, [originalQuestions, selectedOptions]);

  const noQuestionsSelected = filteredQuestions.length === 0;

  useEffect(() => {
    const total = filteredQuestions.length;
    setStartLimit(total > 0 ? 1 : 0);
    setEndLimit(total > 0 ? total : 0);
  }, [filteredQuestions.length]);

  const handleSliderChange = (newVal) => {
    setStartLimit(newVal[0]);
    setEndLimit(newVal[1]);
  };

  function importAll(r) {
    let files = {};
    r.keys().forEach(item => { files[item.replace('./', '')] = r(item); });
    return files;
  }

  useEffect(() => {
    document.title = 'Select Sub-Category — QuizWhiz';
    const quizes = importAll(require.context('./resources/', true, /\.(json)$/));
    const keys = Object.keys(quizes);
    let allQuestions = [];
    for (let i = 0; i < keys.length; i++) {
      if (keys[i].includes(category) && keys[i].includes('Grade' + level)) {
        const quizData = quizes[keys[i]];
        const questions = quizData.questions || quizData;
        allQuestions = allQuestions.concat(questions);
      }
    }
    setOriginalQuestions(allQuestions);
    const uniqueSubs = [...new Set(allQuestions.map(q => q.subcategory))].filter(Boolean);
    setSubCategories(uniqueSubs.map(sub => ({ value: sub, label: sub })));
    setIsLoading(false);
  }, [category, level]);

  const handleSelectChange = (selected) => {
    if (selected && selected.length > 0) {
      if (selected[selected.length - 1].value === 'select-all') {
        setSelectedOptions(
          selected.length - 1 === subCategories.length ? [] : subCategories
        );
        return;
      }
    }
    setSelectedOptions(selected);
  };

  const formatTimeDisplay = (seconds) => {
    if (seconds === 0) return 'No time limit';
    if (seconds >= 60) {
      const m = seconds / 60;
      return `${m === Math.floor(m) ? m : m.toFixed(1)} min`;
    }
    return `${seconds} sec`;
  };

  const formatTotalTime = (seconds) => {
    if (seconds === 0) return 'No time limit';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0 && s > 0) return `${m} min ${s} sec`;
    if (m > 0) return `${m} min`;
    return `${s} sec`;
  };

  const sLimitNum = typeof startLimit === 'number' ? startLimit : (parseInt(startLimit, 10) || 1);
  const eLimitNum = typeof endLimit === 'number' ? endLimit : (parseInt(endLimit, 10) || filteredQuestions.length || 1);

  const startPractice = () => {
    let questionsToPractice = filteredQuestions.slice(Math.max(0, sLimitNum - 1), Math.max(0, eLimitNum));
    if (random) {
      questionsToPractice = [...questionsToPractice].sort(() => Math.random() - 0.5);
    }
    if (questionsToPractice.length === 0) return;

    history.push({
      pathname: '/practicequiz',
      state: {
        completed: 0,
        eventtype: 'Practice',
        evenstartdate: '2020-0-31',
        levels: ['Grade' + level],
        category,
        specificcategory: category,
        totalquestions: questionsToPractice.length,
        level: 'Grade' + level,
        name: `${category} — Custom Practice`,
        eventdisplay: '',
        qualificationno: questionsToPractice.length > 0 ? questionsToPractice.length - 1 : 0,
        time: timePerQuestion,
        questions: questionsToPractice,
        id: 1,
        randomize: false,
        start_limit: 0,
        end_limit: questionsToPractice.length - 1,
      },
    });
  };

  const numQuestionsInRange = noQuestionsSelected ? 0 : Math.max(0, eLimitNum - sLimitNum + 1);
  const totalTimeInSeconds = timePerQuestion === 0 ? 0 : timePerQuestion * numQuestionsInRange;

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', background: CREAM }}>
        <Lottie options={defaultOptions} height={280} width={280} />
      </div>
    );
  }

  const isMath = category === 'MathBowl';
  const displayTitle = isMath ? 'MathBowl' : category;
  const selectOptions = [{ value: 'select-all', label: '✓ Select All' }, ...subCategories];

  return (
    <div className={pageStyles}>
      {/* ── Hero Banner ── */}
      <div className={heroStyles}>
        <div className={heroImageWrap}>
          <img src={isMath ? mathImg : generalMathImg} alt={displayTitle} />
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className={heroBadge}>
            <span /> Practice Mode
          </div>
          <h1 className={heroTitle}>{displayTitle}</h1>
          <p className={heroSub}>
            Grade {level} &nbsp;·&nbsp; {originalQuestions.length} questions available
          </p>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className={contentWrap}>

        {/* Topic Selection Card */}
        <div className={cardStyles} style={{ position: 'relative', zIndex: 10 }}>
          <div className={sectionLabel}>Step 1</div>
          <div className={sectionTitle}>
            {isMath ? 'Choose topics to practise' : `Select sub-categories for ${displayTitle}`}
          </div>

          <Select
            isMulti
            name="subcategories"
            options={selectOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            onChange={handleSelectChange}
            value={selectedOptions}
            styles={selectStyles}
            placeholder={isMath ? 'Choose topics…' : 'Choose sub-categories…'}
            components={{ Option, ValueContainer }}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
          />

          {selectedOptions.length > 0 && (
            <div style={{ marginTop: 12, fontSize: '0.85rem', color: TEXT_MID }}>
              <strong style={{ color: ORANGE }}>{filteredQuestions.length}</strong> questions match your selection.
            </div>
          )}
        </div>

        {/* Quiz Settings Card */}
        <div className={cardStyles}>
          <div className={sectionLabel}>Step 2</div>
          <div className={sectionTitle}>Configure your session</div>

          {/* Shuffle */}
          <div className={rowStyles}>
            <span className={rowLabel}>Shuffle Questions</span>
            <label className={switchWrap}>
              <input type="checkbox" onChange={() => setRandom(!random)} checked={random} />
              <span className="slider" />
            </label>
          </div>

          <div className={divider} />

          {/* Question range */}
          <div className={rowStyles} style={{ flexWrap: 'wrap', gap: '12px' }}>
            <span className={rowLabel}>No. of Questions</span>
            <div className={sliderContainerStyles}>
              <input
                type="number"
                min={noQuestionsSelected ? 0 : 1}
                max={noQuestionsSelected ? 0 : eLimitNum}
                value={noQuestionsSelected ? '' : startLimit}
                placeholder={noQuestionsSelected ? '—' : ''}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setStartLimit('');
                    return;
                  }
                  let val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= (eLimitNum || filteredQuestions.length)) {
                    setStartLimit(val);
                  }
                }}
                onBlur={() => {
                  let val = parseInt(startLimit, 10);
                  if (isNaN(val) || val < 1) setStartLimit(1);
                  else if (val > eLimitNum) setStartLimit(eLimitNum);
                }}
                style={numInputStyles}
                disabled={noQuestionsSelected}
              />
              <div style={{ flex: 1, minWidth: 100 }}>
                <Slider
                  range
                  trackStyle={{ backgroundColor: ORANGE, height: 6 }}
                  handleStyle={[
                    { borderColor: ORANGE, height: 16, width: 16, marginTop: -5, backgroundColor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                    { borderColor: ORANGE, height: 16, width: 16, marginTop: -5, backgroundColor: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }
                  ]}
                  railStyle={{ backgroundColor: 'rgba(241, 102, 2, 0.15)', height: 6 }}
                  min={noQuestionsSelected ? 0 : 1}
                  max={filteredQuestions.length || 1}
                  value={[
                    noQuestionsSelected ? 0 : sLimitNum,
                    noQuestionsSelected ? 0 : eLimitNum
                  ]}
                  onChange={handleSliderChange}
                  disabled={noQuestionsSelected}
                  style={{ flex: 1, minWidth: 60 }}
                />
              </div>
              <input
                type="number"
                min={noQuestionsSelected ? 0 : sLimitNum}
                max={filteredQuestions.length || 1}
                value={noQuestionsSelected ? '' : endLimit}
                placeholder={noQuestionsSelected ? '—' : ''}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setEndLimit('');
                    return;
                  }
                  let val = parseInt(e.target.value, 10);
                  let maxLim = filteredQuestions.length || 1;
                  if (!isNaN(val) && val >= (sLimitNum || 1) && val <= maxLim) {
                    setEndLimit(val);
                  }
                }}
                onBlur={() => {
                  let val = parseInt(endLimit, 10);
                  let maxLim = filteredQuestions.length || 1;
                  if (isNaN(val) || val > maxLim) setEndLimit(maxLim);
                  else if (val < sLimitNum) setEndLimit(sLimitNum);
                }}
                style={numInputStyles}
                disabled={noQuestionsSelected}
              />
            </div>
          </div>

          <div className={divider} />

          {/* Time per question */}
          <div className={rowStyles}>
            <span className={rowLabel}>Time per question</span>
            <div className={timeSelectStyles}>
              <select value={timePerQuestion} onChange={(e) => setTimePerQuestion(parseInt(e.target.value))}>
                {timeOptions.map((t) => (
                  <option key={t} value={t}>{formatTimeDisplay(t)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Total time */}
          <div className={totalTimeBar}>
            <span>Total Session Time</span>
            <span>{noQuestionsSelected ? '—' : formatTotalTime(totalTimeInSeconds)}</span>
          </div>
        </div>

        {/* Start Button */}
        <button
          className={startBtn}
          onClick={startPractice}
          disabled={noQuestionsSelected}
        >
          🚀 Start Practice
        </button>
        {noQuestionsSelected && (
          <p className={emptyHint}>Select at least one topic above to begin.</p>
        )}
      </div>
    </div>
  );
}

export default SubCategorySelection;